import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Card,
  Select,
  TextField,
  Checkbox,
  Row,
  Col,
  Button,
  Badge,
} from '@folio/stripes/components';

import ProfessionalPrinterHelper from './ProfessionalPrinterHelper';

const PrintSettings = ({ settings, onSettingsChange }) => {
  const intl = useIntl();
  const printerHelper = new ProfessionalPrinterHelper();

  const handleSettingChange = (field, value) => {
    onSettingsChange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const barcodeFormatOptions = [
    {
      value: 'CODE128',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.barcodeFormat.code128',
        defaultMessage: 'Code128 (Recommended)',
      })
    },
    {
      value: 'CODE39',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.barcodeFormat.code39',
        defaultMessage: 'Code39',
      })
    },
    {
      value: 'EAN13',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.barcodeFormat.ean13',
        defaultMessage: 'EAN13',
      })
    },
    {
      value: 'EAN8',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.barcodeFormat.ean8',
        defaultMessage: 'EAN8',
      })
    },
  ];

  const layoutFormatOptions = [
    {
      value: 'standard',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.layout.standard',
        defaultMessage: 'Standard Layout',
      })
    },
    {
      value: 'compact',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.layout.compact',
        defaultMessage: 'Compact Layout',
      })
    },
    {
      value: 'labels',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.layout.labels',
        defaultMessage: 'Label Sheets',
      })
    },
  ];

  const paperSizeOptions = [
    { value: 'A4', label: 'A4 (210 × 297 mm)' },
    { value: 'Letter', label: 'Letter (8.5 × 11 in)' },
    { value: 'Legal', label: 'Legal (8.5 × 14 in)' },
    { value: 'A5', label: 'A5 (148 × 210 mm)' },
  ];

  const orientationOptions = [
    {
      value: 'portrait',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.orientation.portrait',
        defaultMessage: 'Portrait',
      })
    },
    {
      value: 'landscape',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.orientation.landscape',
        defaultMessage: 'Landscape',
      })
    },
  ];

  const printerModeOptions = [
    {
      value: 'browser',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.printerMode.browser',
        defaultMessage: 'Browser Printing (Basic)',
      })
    },
    {
      value: 'professional',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.printerMode.professional',
        defaultMessage: 'Professional Printer (Recommended)',
      })
    },
    {
      value: 'download',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.settings.printerMode.download',
        defaultMessage: 'Download Print File',
      })
    },
  ];

  const printerTypeOptions = printerHelper.getSupportedPrinterTypes().map(printer => ({
    value: printer.value,
    label: printer.label
  }));

  const isProfessionalMode = settings.printerMode === 'professional';
  const isDownloadMode = settings.printerMode === 'download';
  const isProfessionalSupported = printerHelper.isProfessionalPrintingSupported();

  return (
    <Card style={{ marginBottom: '16px' }}>
      <div style={{ padding: '16px' }}>
        <h4 style={{ marginTop: 0, marginBottom: '16px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.barcodePrinting.settings.title"
            defaultMessage="Print Settings"
          />
        </h4>

        {/* Printer Mode Selection */}
        <Row>
          <Col xs={12}>
            <Select
              label={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.barcodePrinting.settings.printerMode.label',
                defaultMessage: 'Printer Mode',
              })}
              dataOptions={printerModeOptions}
              value={settings.printerMode || 'browser'}
              onChange={(e) => handleSettingChange('printerMode', e.target.value)}
              fullWidth
              marginBottom0
            />
            {!isProfessionalSupported && (
              <div style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>
                <Badge color="warning">
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.barcodePrinting.settings.professionalNotSupported"
                    defaultMessage="Professional printing requires a compatible browser"
                  />
                </Badge>
              </div>
            )}
          </Col>
        </Row>

        {/* Professional Printer Type */}
        {(isProfessionalMode || isDownloadMode) && (
          <Row>
            <Col xs={12}>
              <Select
                label={intl.formatMessage({
                  id: 'ui-plugin-inventory-templates.barcodePrinting.settings.printerType.label',
                  defaultMessage: 'Printer Type',
                })}
                dataOptions={printerTypeOptions}
                value={settings.printerType || 'ZEBRA_ZPL'}
                onChange={(e) => handleSettingChange('printerType', e.target.value)}
                fullWidth
                marginBottom0
              />
            </Col>
          </Row>
        )}

        <Row>
          <Col xs={12}>
            <Select
              label={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.barcodePrinting.settings.barcodeFormat.label',
                defaultMessage: 'Barcode Format',
              })}
              dataOptions={barcodeFormatOptions}
              value={settings.format}
              onChange={(e) => handleSettingChange('format', e.target.value)}
              fullWidth
              marginBottom0
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Select
              label={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.barcodePrinting.settings.layout.label',
                defaultMessage: 'Layout Format',
              })}
              dataOptions={layoutFormatOptions}
              value={settings.layout || 'standard'}
              onChange={(e) => handleSettingChange('layout', e.target.value)}
              fullWidth
              marginBottom0
            />
          </Col>
        </Row>

        <Row>
          <Col xs={6}>
            <Select
              label={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.barcodePrinting.settings.paperSize.label',
                defaultMessage: 'Paper Size',
              })}
              dataOptions={paperSizeOptions}
              value={settings.paperSize}
              onChange={(e) => handleSettingChange('paperSize', e.target.value)}
              fullWidth
              marginBottom0
            />
          </Col>
          <Col xs={6}>
            <Select
              label={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.barcodePrinting.settings.orientation.label',
                defaultMessage: 'Orientation',
              })}
              dataOptions={orientationOptions}
              value={settings.orientation || 'portrait'}
              onChange={(e) => handleSettingChange('orientation', e.target.value)}
              fullWidth
              marginBottom0
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <TextField
              label={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.barcodePrinting.settings.copies.label',
                defaultMessage: 'Number of Copies',
              })}
              type="number"
              min="1"
              max="10"
              value={settings.copies}
              onChange={(e) => handleSettingChange('copies', parseInt(e.target.value, 10) || 1)}
              fullWidth
              marginBottom0
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Checkbox
              label={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.barcodePrinting.settings.includeText.label',
                defaultMessage: 'Include barcode text',
              })}
              checked={settings.includeText}
              onChange={(e) => handleSettingChange('includeText', e.target.checked)}
            />
          </Col>
        </Row>

        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}
        >
          <strong>
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcodePrinting.settings.note.title"
              defaultMessage="Note:"
            />
          </strong>
          <div style={{ marginTop: '4px' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcodePrinting.settings.note.description"
              defaultMessage="Barcode printing requires a compatible printer connected to your system."
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

PrintSettings.propTypes = {
  settings: PropTypes.shape({
    format: PropTypes.string.isRequired,
    layout: PropTypes.string,
    includeText: PropTypes.bool.isRequired,
    copies: PropTypes.number.isRequired,
    paperSize: PropTypes.string.isRequired,
    orientation: PropTypes.string,
    printerMode: PropTypes.string,
    printerType: PropTypes.string,
  }).isRequired,
  onSettingsChange: PropTypes.func.isRequired,
};

export default PrintSettings;
